import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0] ?? ''}${(parts[parts.length - 1] as string)[0] ?? ''}`.toUpperCase();
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * Department swatches, built only from the Tive blues and greens and their
 * documented 20% tint ramp. Red and orange are deliberately excluded: they mean
 * "moved" and "leaving" on the chart, and a red avatar would read as a change.
 *
 * Every pair clears 4.5:1 (the initials are 11px bold), verified against the
 * brand values rather than eyeballed.
 */
const PALETTE = [
  { bg: '#d9e7fd', fg: '#2e5dac', dbg: '#122545', dfg: '#689df7' }, // Tive Blue 20%
  { bg: '#d2f3e8', fg: '#117453', dbg: '#083627', dfg: '#4acea2' }, // Tive Dark Green 20%
  { bg: '#d3ddf3', fg: '#1f4eb1', dbg: '#0e2553', dfg: '#7a9adc' }, // Tive Dark Blue 20%
  { bg: '#d9f8ee', fg: '#206f55', dbg: '#123e30', dfg: '#65e5bb' }, // Tive Green 20%
  { bg: '#b3cefb', fg: '#285093', dbg: '#1c3867', dfg: '#8eb6f9' }, // Tive Blue 40%
  { bg: '#a5e7d1', fg: '#0e6146', dbg: '#0c513a', dfg: '#4acea2' }, // Tive Dark Green 40%
  { bg: '#a7bce8', fg: '#1b469e', dbg: '#0a1837', dfg: '#7a9adc' }, // Tive Dark Blue 40%
  { bg: '#b2f2dd', fg: '#206f55', dbg: '#1a5d47', dfg: '#65e5bb' }, // Tive Green 40%
  { bg: '#8eb6f9', fg: '#1c3f79', dbg: '#17325c', dfg: '#8eb6f9' }, // Tive Blue 60%
  { bg: '#71b29c', fg: '#0d3d2c', dbg: '#0f4634', dfg: '#4acea2' }, // Tive Dark Green 60%
  { bg: '#7a9adc', fg: '#14336f', dbg: '#142c5e', dfg: '#7a9adc' }, // Tive Dark Blue 60%
  { bg: '#77dab9', fg: '#12503c', dbg: '#17513f', dfg: '#65e5bb' }, // Tive Green 60%
];

/**
 * FNV-1a. The multiply-by-31 version this replaced put "Finance" and "Software
 * Development" on the same swatch, which is exactly the pair a chart coloured
 * by department has to tell apart.
 */
export function hashKey(value: string): number {
  let out = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    out ^= value.charCodeAt(i);
    out = Math.imul(out, 0x01000193);
  }
  return Math.abs(out | 0);
}

export function departmentColor(department: string | null, dark: boolean): { fg: string; bg: string } {
  const entry = PALETTE[hashKey(department ?? 'Unassigned') % PALETTE.length] as (typeof PALETTE)[number];
  return dark ? { fg: entry.dfg, bg: entry.dbg } : { fg: entry.fg, bg: entry.bg };
}

/**
 * Whether somebody works from an office or from wherever they are.
 *
 * Rippling's LOCATION is free text, so this reads it rather than trusting a
 * flag: "Remote", "Remote - EMEA" and "Work from home" all mean the same thing,
 * and everything else is the name of a place we have a desk in.
 */
export function isRemote(location: string | null): boolean {
  return /\b(remote|wfh|work from home|home office)\b/i.test(location ?? '');
}

/** "Boston", "Remote · Ireland", "No location" — what a card shows. */
export function placeLabel(location: string | null, country: string | null): string {
  const place = location?.trim();
  if (!place) return country?.trim() || 'No location';
  // The country only earns its space when the location does not already imply
  // it: "Remote" says nothing about where, "Boston" says plenty.
  if (isRemote(place) && country?.trim()) return `${place} · ${country.trim()}`;
  return place;
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const dateOnly = iso.length <= 10;
  const date = new Date(dateOnly ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    // A start date is a calendar date, not an instant. It is parsed as UTC
    // midnight, so it has to be rendered in UTC too — otherwise every start
    // date reads a day early for anyone west of Greenwich, which is most of us.
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  });
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
}
